/**
 * Products Page - List and manage concrete mix designs / products
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import productsService, { ProductListItem, ProductSearchParams } from '../services/products';
import EmptyState from '../components/EmptyState';
import { useAuth } from '../context/AuthContext';
import { hasPermission, isSuperAdmin } from '../utils/userPermissions';
import {
  HEADER_CELL_SX,
  ListToolbar,
  NameCell,
  StatusDot,
  RowActionsMenu,
  DetailStrip,
  SelectableTableRow,
  SelectableCard,
  useRowSelection,
  type RowAction,
} from '../components/ListPageShared';
import { maturityDatumOrQ, maturityEquationName } from '../utils/maturityEquation';

type StatusFilter = 'all' | 'active' | 'inactive';

const Products: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const canManage = hasPermission(user, 'mix_designs.create');
  const canDelete = isSuperAdmin(user);
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  // Search (debounced — pagination is server-side) and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('active');
  const [togglingId, setTogglingId] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);

  // Active/inactive totals for the status switch (respect the search query)
  const [statusCounts, setStatusCounts] = useState({ active: 0, inactive: 0 });

  // Row selection + dialogs
  const selection = useRowSelection();
  const [deleteTarget, setDeleteTarget] = useState<ProductListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: ProductSearchParams = {
        code: debouncedSearch || undefined,
        is_active: statusFilter === 'all' ? undefined : statusFilter === 'active',
        page: page + 1,
        page_size: pageSize,
      };

      const response = await productsService.searchProducts(params);
      setProducts(response.items);
      setRowCount(response.total);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('products.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statusFilter, page, pageSize]);

  // Status counts: two page_size-1 queries piggyback on the server total
  const loadStatusCounts = useCallback(async () => {
    try {
      const base = { code: debouncedSearch || undefined, page: 1, page_size: 1 };
      const [active, inactive] = await Promise.all([
        productsService.searchProducts({ ...base, is_active: true }),
        productsService.searchProducts({ ...base, is_active: false }),
      ]);
      setStatusCounts({ active: active.total, inactive: inactive.total });
    } catch {
      /* switch falls back to stale counts; the list itself reports errors */
    }
  }, [debouncedSearch]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    loadStatusCounts();
  }, [loadStatusCounts]);

  // Reset to the first page when search or status changes
  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter]);

  const refresh = () => {
    loadProducts();
    loadStatusCounts();
  };

  const handleAdd = () => {
    navigate('/products/new');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await productsService.deleteProduct(deleteTarget.product_id);
      setSnackbar({ msg: t('products.deleteSuccess'), severity: 'success' });
      setDeleteTarget(null);
      refresh();
    } catch (err: any) {
      setSnackbar({ msg: err.response?.data?.detail || t('products.failedToDelete'), severity: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (product: ProductListItem) => {
    const nextActive = !(product.is_active !== false);
    setTogglingId(product.product_id);
    try {
      await productsService.updateProduct(product.product_id, { is_active: nextActive });
      setSnackbar({
        msg: nextActive
          ? t('products.activated', 'Mix design activated')
          : t('products.deactivated', 'Mix design deactivated'),
        severity: 'success',
      });
      refresh();
    } catch (err: any) {
      setSnackbar({ msg: err.response?.data?.detail || t('products.toggleFailed', 'Failed to update status'), severity: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  // Matches the Projects list: "12 Jul 2026 · username"
  const formatCreated = (row: ProductListItem): string => {
    if (!row.date_create) return '—';
    const iso = row.date_create;
    const hasZone = /Z|[+-]\d{2}:?\d{2}$/.test(iso);
    const d = new Date(hasZone ? iso : `${iso}Z`);
    if (Number.isNaN(d.getTime())) return '—';
    const date = d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
    return row.create_by ? `${date} · ${row.create_by}` : date;
  };

  // -------------------------------------------------------------------------
  // Shared render fragments
  // -------------------------------------------------------------------------

  const isActive = (product: ProductListItem) => product.is_active !== false;

  const actionsFor = (product: ProductListItem) => {
    const view: RowAction = {
      key: 'view',
      label: t('users.viewDetails', 'View Details'),
      icon: <ViewIcon fontSize="small" />,
      onClick: () => navigate(`/products/${product.product_id}`),
    };
    const edit: RowAction | false = canManage && {
      key: 'edit',
      label: t('common.edit', 'Edit'),
      icon: <EditIcon fontSize="small" />,
      onClick: () => navigate(`/products/${product.product_id}/edit`),
    };
    const toggle: RowAction | false = canManage && {
      key: 'toggle',
      label: isActive(product) ? t('products.deactivate', 'Deactivate') : t('products.activate', 'Activate'),
      icon: isActive(product) ? <ToggleOffIcon fontSize="small" /> : <ToggleOnIcon fontSize="small" />,
      disabled: togglingId === product.product_id,
      onClick: () => handleToggleActive(product),
    };
    const remove: RowAction | false = canDelete && {
      key: 'delete',
      label: t('common.delete', 'Delete'),
      icon: <DeleteIcon fontSize="small" />,
      color: 'error',
      onClick: () => setDeleteTarget(product),
    };
    return { menu: [view, edit, toggle, remove], strip: [view, edit] };
  };

  const statusDot = (product: ProductListItem) => (
    <StatusDot active={isActive(product)} activeLabel={t('common.active', 'Active')} inactiveLabel={t('common.inactive', 'Inactive')} />
  );

  const detailStrip = (product: ProductListItem) => (
    <DetailStrip
      // Grade, equation and Datum / Q are already columns; on mobile they
      // live on the View page
      fields={[{ label: t('products.supplierPlant', 'Supplier Plant'), value: product.supplier_plant_name || '—' }]}
      description={
        <>
          {product.description?.trim() || t('products.noDescription', 'No description')}
          <Typography variant="caption" color="text.disabled" display="block">
            {t('products.created', 'Created {{when}}', { when: formatCreated(product) })}
          </Typography>
        </>
      }
      actions={actionsFor(product).strip}
    />
  );

  const emptyState = (
    <EmptyState
      title={t('products.noProductsFound')}
      description={t('products.addFirstProduct')}
      icon={<SearchIcon />}
      actionLabel={canManage ? t('products.newProduct') : undefined}
      onAction={canManage ? handleAdd : undefined}
    />
  );

  const paginationProps = {
    rowsPerPageOptions: [10, 20, 50, 100],
    component: 'div' as const,
    count: rowCount,
    rowsPerPage: pageSize,
    page,
    onPageChange: (_: unknown, newPage: number) => setPage(newPage),
    onRowsPerPageChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setPageSize(parseInt(e.target.value, 10));
      setPage(0);
    },
  };

  if (loading && products.length === 0 && rowCount === 0 && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  const filterSummary =
    debouncedSearch || statusFilter !== 'all'
      ? `Showing ${products.length} of ${rowCount} mix designs (filtered)`
      : `Total mix designs: ${rowCount}`;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ListToolbar
        search={searchQuery}
        onSearch={setSearchQuery}
        searchPlaceholder={t('products.searchPlaceholder')}
        statusOptions={[
          { key: 'all', label: t('products.allMixDesigns', 'All Mix Designs'), count: statusCounts.active + statusCounts.inactive },
          { key: 'active', label: t('common.active', 'Active'), count: statusCounts.active },
          { key: 'inactive', label: t('common.inactive', 'Inactive'), count: statusCounts.inactive },
        ]}
        status={statusFilter}
        onStatus={setStatusFilter}
        onRefresh={refresh}
        loading={loading}
        addLabel={canManage ? t('products.newProduct') : undefined}
        onAdd={handleAdd}
      />

      {/* Mobile card list */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {products.length === 0 && !loading ? (
            <Paper sx={{ p: 3 }}>{emptyState}</Paper>
          ) : (
            products.map((product) => (
              <SelectableCard
                key={product.product_id}
                rowRef={selection.rowRef(product.product_id)}
                selected={selection.selectedId === product.product_id}
                onClick={() => selection.toggle(product.product_id)}
                header={
                  <>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <NameCell name={product.code} subtitle={product.description} />
                    </Box>
                    {statusDot(product)}
                    <RowActionsMenu actions={actionsFor(product).menu} />
                  </>
                }
                body={
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    {product.strength_grade != null && (
                      <Typography variant="caption" color="text.secondary">
                        {t('products.strengthMpa', 'Grade Strength')}: {product.strength_grade}
                      </Typography>
                    )}
                    {product.rev && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        Rev {product.rev}
                      </Typography>
                    )}
                  </Box>
                }
                detail={detailStrip(product)}
              />
            ))
          )}
          <TablePagination
            {...paginationProps}
            labelRowsPerPage=""
            sx={{ '& .MuiTablePagination-selectLabel': { display: 'none' } }}
          />
        </Box>
      ) : (
        /* Desktop table: click a row to select it and show its detail strip */
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={HEADER_CELL_SX}>{t('products.code', 'Mix / Recipe')}</TableCell>
                <TableCell sx={HEADER_CELL_SX} align="center">{t('products.strengthMpa', 'Grade Strength')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('products.maturityEquation', 'Maturity Equation')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('products.datumQ', 'Datum / Q')}</TableCell>
                <TableCell sx={HEADER_CELL_SX} align="center">{t('products.rev', 'Revision')}</TableCell>
                <TableCell sx={HEADER_CELL_SX}>{t('products.dateCreated', 'Date Created')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 110 }}>{t('common.status', 'Status')}</TableCell>
                <TableCell sx={{ ...HEADER_CELL_SX, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    {loading ? (
                      <Typography color="text.secondary">{t('common.loading')}</Typography>
                    ) : (
                      emptyState
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <SelectableTableRow
                    key={product.product_id}
                    rowRef={selection.rowRef(product.product_id)}
                    selected={selection.selectedId === product.product_id}
                    onClick={() => selection.toggle(product.product_id)}
                    colSpan={8}
                    detail={detailStrip(product)}
                  >
                    <TableCell sx={{ maxWidth: 320 }}>
                      <NameCell name={product.code} subtitle={product.description} />
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ color: product.strength_grade != null ? 'text.primary' : 'text.disabled' }}>
                        {product.strength_grade ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: maturityEquationName(product) ? 'text.primary' : 'text.disabled' }} noWrap>
                        {maturityEquationName(product) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: maturityDatumOrQ(product) ? 'text.primary' : 'text.disabled' }} noWrap>
                        {maturityDatumOrQ(product) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', color: product.rev ? 'text.primary' : 'text.disabled' }}>
                        {product.rev || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {formatCreated(product)}
                      </Typography>
                    </TableCell>
                    <TableCell>{statusDot(product)}</TableCell>
                    <TableCell align="right" sx={{ pr: 1 }}>
                      <RowActionsMenu actions={actionsFor(product).menu} />
                    </TableCell>
                  </SelectableTableRow>
                ))
              )}
            </TableBody>
          </Table>
          {/* Footer: filter summary on the left, pagination on the right */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {filterSummary}
            </Typography>
            <TablePagination {...paginationProps} labelRowsPerPage={t('common.rowsPerPage')} />
          </Box>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        disableEscapeKeyDown={deleting}
      >
        <DialogTitle>{t('products.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('products.deleteWarning')}
          </Alert>
          <DialogContentText>
            {t('products.deleteConfirmation', { code: deleteTarget?.code, description: deleteTarget?.description })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? t('products.deleting', 'Deleting...') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar !== null} autoHideDuration={4000} onClose={() => setSnackbar(null)}>
        <Alert
          severity={snackbar?.severity ?? 'success'}
          onClose={() => setSnackbar(null)}
          variant="filled"
        >
          {snackbar?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Products;
